import Section from 'app/frontend/base/components/Section'
import CharacterTable from './components/CharacterTable'
import { useEffect, useMemo } from 'react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useLogo, useRefreshData } from 'app/frontend/base/helpers'
import { refreshPersonalCharacters } from 'app/frontend/base/api/user'
import { Button } from '@/components/ui/button'
import Timestamp from 'app/frontend/base/components/Timestamp'
import ActiveRegions from './components/ActiveRegions'
import { useSearchParams } from 'react-router-dom'

const UserCharacters = () => {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const {
    data: { user, impersonating },
  } = usePageInfo()

  const { Logo } = useLogo()

  const { refreshing, refreshData } = useRefreshData({
    retriever: refreshPersonalCharacters,
    queryToInvalidate: ['pageInfo'],
  })

  useEffect(() => {
    if (searchParams.get('newToken') === 'true') {
      refreshData()
    }
  }, [])

  const actionableCharacters = useMemo(() => {
    return (
      user?.characterReferences?.filter((character) => character.actionable) ||
      []
    )
  }, [user?.characterReferences])

  const tokenExpired = user && dayjs(user.oauthExpiresAt).isBefore(dayjs())

  if (!user) {
    return null
  }

  return (
    <Section title='Profile'>
      <div className='flex items-start justify-between mb-4 gap-4 flex-wrap'>
        <h2>Characters</h2>

        <div
          className={`flex flex-col items-end grow ${
            impersonating ? 'pointer-events-none opacity-30' : ''
          }`}
        >
          <a
            href={
              tokenExpired
                ? `/auth/bnet?origin=${encodeURIComponent(
                    '/profile/characters?newToken=true',
                  )}`
                : undefined
            }
            onClick={
              tokenExpired
                ? () =>
                    sessionStorage.setItem(
                      'scrollPosition',
                      window.scrollY.toString(),
                    )
                : () => refreshData()
            }
          >
            <Button
              variant='outline'
              className='flex items-center gap-3 min-w-[9.5rem]'
              loading={refreshing}
              spinner='blizzard'
            >
              <Logo className='w-6 h-6' />
              {t('shared.update-now')}
            </Button>
          </a>

          <div className='flex items-center gap-4 mt-2'>
            <div className='flex gap-1 text-xs text-right'>
              <span>{t('shared.last-updated')}</span>
              <Timestamp time={user.characterDataRefreshedAt} />
            </div>

            <ActiveRegions
              disabled={refreshing}
              updateCharacters={tokenExpired ? () => null : () => refreshData()}
            />
          </div>
        </div>
      </div>

      {actionableCharacters.length ? (
        <CharacterTable
          actionable={true}
          characterReferences={actionableCharacters}
        />
      ) : null}

      <CharacterTable
        actionable={false}
        characterReferences={user.characterReferences ?? []}
      />
    </Section>
  )
}

export default UserCharacters
